import { memo, useCallback, useEffect, useRef, useState } from 'react';

import checkmark from "../img/checkmark.svg";

import formatDistance from "../functions/formatDistance";
import formatTime from "../functions/formatTime.js";

import tl from "../translations";

import Tag from './Tag';

function TrailCard(props) {

  const myRef = useRef(null);
  const fullImageRef = useRef(null);
  const videoRef = useRef(null);
  const scrollFrame = useRef(null);
  const mediaPriority = props.mediaPriority === true;

  const [rect, setRect] = useState({top: 0, bottom: 0, height: 400});

  const [highResLoaded, setHighResLoaded] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [isNearViewport, setIsNearViewport] = useState(mediaPriority);
  const [shouldLoadMedia, setShouldLoadMedia] = useState(mediaPriority);

  const options = props.options || {};


  useEffect(() => {
    const measureCard = () => {
      scrollFrame.current = null;

      if (!myRef.current) {
        return;
      }

      const nextRect = myRef.current.getBoundingClientRect();

      setRect((previousRect) => {
        if (
          Math.abs(previousRect.top - nextRect.top) < 1 &&
          Math.abs(previousRect.bottom - nextRect.bottom) < 1 &&
          Math.abs(previousRect.height - nextRect.height) < 1
        ) {
          return previousRect;
        }

        return {
          top: nextRect.top,
          bottom: nextRect.bottom,
          height: nextRect.height,
        };
      });
    };

    const requestMeasure = () => {
      if (scrollFrame.current === null) {
        scrollFrame.current = window.requestAnimationFrame(measureCard);
      }
    };

    window.addEventListener('scroll', requestMeasure, { passive: true });
    window.addEventListener('resize', requestMeasure);
    requestMeasure(); // Initial call to getBoundingClientRect

    //console.log(window.innerHeight);

    return () => {
      window.removeEventListener('scroll', requestMeasure);
      window.removeEventListener('resize', requestMeasure);

      if (scrollFrame.current !== null) {
        window.cancelAnimationFrame(scrollFrame.current);
      }
    };

    
  }, []);

  useEffect(() => {
    if (mediaPriority) {
      setShouldLoadMedia(true);
    }

    if (!myRef.current || !('IntersectionObserver' in window)) {
      setIsNearViewport(true);
      setShouldLoadMedia(true);
      return;
    }

    const observer = new IntersectionObserver(([entry]) => {
      setIsNearViewport(entry.isIntersecting);
    }, {
      rootMargin: '500px 0px',
      threshold: 0,
    });

    observer.observe(myRef.current);

    return () => {
      observer.disconnect();
    };
  }, [mediaPriority]);

  useEffect(() => {
    if (shouldLoadMedia || !isNearViewport) {
      return;
    }

    const mediaLoadTimer = window.setTimeout(() => {
      setShouldLoadMedia(true);
    }, props.mediaLoadDelay || 0);

    return () => {
      window.clearTimeout(mediaLoadTimer);
    };
  }, [isNearViewport, props.mediaLoadDelay, shouldLoadMedia]);

  function getParallaxOffset() {
    if (rect.bottom < 0) {
      return 0;  // Image is above the viewport, no parallax effect yet
    }
    if (rect.top > window.innerHeight) {
      return -20;  // Image is below the viewport, start with -20%
    }
      //console.log("visible!");
      let delta = window.innerHeight + rect.height;
      let scrollvalue = (rect.top + rect.height) / delta;
      //console.log(scrollvalue);
      return scrollvalue * -20;
    
  }

  const blurSrc = `${process.env.REACT_APP_CONTENT_URL}${props.slug}/${props.id}/card-small.png`;
  const fullSrc = `${process.env.REACT_APP_CONTENT_URL}${props.slug}/${props.id}/card.png`;

  const videoSrc = `${process.env.REACT_APP_CONTENT_URL}${props.slug}/${props.id}/card.mp4`;


  useEffect(() => {
    setHighResLoaded(false);
  }, [fullSrc]);

  const markHighResLoaded = useCallback((image) => {
    const loadedSrc = image.currentSrc || image.src;

    const markLoaded = () => {
      if ((image.currentSrc || image.src) === loadedSrc) {
        setHighResLoaded(true);
      }
    };

    if (image.decode) {
      image.decode().then(markLoaded).catch(markLoaded);
      return;
    }

    markLoaded();
  }, []);

  function handleHighResLoad(event) {
    markHighResLoaded(event.currentTarget);
  }

  useEffect(() => {
    const image = fullImageRef.current;

    if (!image || !shouldLoadMedia || !image.complete || image.naturalWidth === 0) {
      return;
    }

    markHighResLoaded(image);
  }, [fullSrc, markHighResLoaded, shouldLoadMedia]);

  useEffect(() => {
    setVideoLoaded(false);
  }, [videoSrc]);

  useEffect(() => {
    const video = videoRef.current;

    if (!video || !shouldLoadMedia || !options.usevideo) {
      return;
    }

    if (isNearViewport) {
      const playPromise = video.play();

      if (playPromise !== undefined) {
        playPromise.catch(() => {});
      }
    } else {
      video.pause();
    }
  }, [isNearViewport, options.usevideo, shouldLoadMedia, videoSrc]);

  const parallaxOffset = getParallaxOffset();
  

  return (
    <div ref={myRef} className={`trailcard trailcard-${props.id}`} /*style={{backgroundImage: `url(${props.img})` }}*/>

      {/*<img loading="lazy" src={props.img} style={{width: '100%', height: '120%', position: "absolute", objectFit: 'cover', top: getParallaxOffset() + '%'}} />*/}
      
        <img
          loading={mediaPriority ? "eager" : "lazy"}
          decoding="async"
          alt=""
          src={blurSrc}
          className='parallaxcarditem parallaxcarditemlowres'
          style={{
            transform: `translate3d(0, ${parallaxOffset}%, 0)`,
          }}
        />
        {shouldLoadMedia && (
        <img
          ref={fullImageRef}
          loading={mediaPriority ? "eager" : "lazy"}
          decoding="async"
          alt=""
          src={fullSrc}
          className='parallaxcarditem parallaxcarditemfullres'
          style={{
            transform: `translate3d(0, ${parallaxOffset}%, 0)`,
            opacity: highResLoaded ? 1 : 0,
          }}
          onLoad={handleHighResLoad}
        />
        )}
        {options.usevideo && shouldLoadMedia && (
        <video
          ref={videoRef}
          autoPlay={isNearViewport}
          loop
          muted
          playsInline
          preload={mediaPriority ? "metadata" : "none"}
          className={`parallaxcarditem parallaxcarditemvideo`}
          style={{
            transform: `translate3d(0, ${parallaxOffset}%, 0)`,
            opacity: videoLoaded ? 1 : 0,
          }}
          onLoadedData={() => setVideoLoaded(true)}
          onCanPlay={() => setVideoLoaded(true)}
        >
          <source src={videoSrc} type="video/mp4" />
        </video>
      )}
      

      <div className='cardtagcontainer'>
      {props.customcardtag !== "" && <div className={`tag`}>{props.customcardtag}</div>}
      {
        Array.isArray(props.tags) && props.tags.filter(item => item.featured).map((tag, index) => <Tag key={index} tag={tag.tag} lang={props.lang} />)
      }
      </div>


      

      {options?.uselogo === false ?
      <h3 style={{color: options?.textcolor ?? "#fff"}}>{props.title}</h3>
      :
      <img className="trailcardlogo" src={props.logo} alt={props.title} style={{width: props.logoscale+"%"}} />
      }
     
      {props.done ? <div className="carddoneicon"><img src={checkmark} alt="" /></div> : null}
      <p style={{color: options?.textcolor ?? "#fff"}}>{props.stations} {props.stations === 1 ? tl.station_singular[props.lang] : tl.stations[props.lang]} · {formatTime(props.time)} {tl.hours[props.lang]} · {formatDistance(props.distance)} km</p>

      {/* Buttons auf der Card - is too much
      <div className='cardbuttoncont'>
        <div className='cardbutton cardbutton-secondary'>Details ansehen</div>
        <div className='cardbutton cardbutton-primary'>Jetzt starten</div>
      </div>
      */}

      {options?.usegradient === false ? null : <div className="trailcardgradient" style={{ background: `linear-gradient(0deg, ${options?.gradientcolor ?? "#000000b3"} 0%, rgba(0, 0, 0, 0) 100%)` }}></div>}
    </div>
  );
}

export default memo(TrailCard);
